import { useState } from 'react';
import { ReportResult } from '../../lib/api';
import { DataTable, isCurrencyColumn, isAgeColumn } from '../Charts/DataTable';
import { BarChart } from '../Charts/BarChart';
import { LineChart } from '../Charts/LineChart';
import { StackedBarChart } from '../Charts/StackedBarChart';
import { Button } from '../Common/Button';
import { Modal } from '../Common/Modal';
import { exportToExcel, exportToPdf } from '../../lib/export';
import { api } from '../../lib/api';
import { showToast } from '../../hooks/useToast';
import { FreshnessBanner } from '../Layout/FreshnessBanner';

interface AIResult extends ReportResult {
  sql: string;
  suggestedName: string;
}

interface Props {
  result: AIResult | null;
}

export function ResultPanel({ result }: Props) {
  const [chartType, setChartType] = useState<string | null>(null);
  const [reportName, setReportName] = useState<string>('');
  const [saveOpen, setSaveOpen] = useState(false);

  if (!result) {
    return (
      <div className="bg-surface border border-border rounded-lg h-full flex items-center justify-center">
        <div className="text-sm text-text-muted">Describe a report in the chat to get started.</div>
      </div>
    );
  }

  const effectiveChart = chartType ?? result.chartType;
  const displayName = reportName || result.suggestedName || 'Custom report';

  const xKey = result.columns[0];
  const numericCols = result.columns.filter(c => isCurrencyColumn(c) || (!isAgeColumn(c) && typeof result.rows[0]?.[c] === 'number'));
  const yKey = numericCols[0] ?? result.columns[1];

  return (
    <div className="bg-surface border border-border rounded-lg flex flex-col h-full">
      <div className="px-4 py-3 border-b border-border-light flex items-center justify-between">
        <input
          value={displayName}
          onChange={e => setReportName(e.target.value)}
          className="text-sm font-medium text-text-primary bg-transparent focus:outline-none flex-1 mr-3"
        />
        <div className="flex items-center gap-2">
          <Button size="sm" onClick={() => setSaveOpen(true)}>Save</Button>
          <Button size="sm" onClick={() => exportToExcel({
            reportName: displayName, params: {}, columns: result.columns, rows: result.rows, runAt: new Date().toISOString(),
          })}>Excel</Button>
          <Button size="sm" onClick={() => exportToPdf({
            reportName: displayName, params: {}, columns: result.columns, rows: result.rows, runAt: new Date().toISOString(),
          })}>PDF</Button>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-4 space-y-3">
        {result.meta.isStale && (
          <FreshnessBanner ageHours={result.meta.ageHours} stagedAt={result.meta.stagedAt} />
        )}

        <div className="flex items-center gap-1">
          {(['bar', 'line', 'stacked', 'table'] as const).map(ct => (
            <button
              key={ct}
              onClick={() => setChartType(ct)}
              className={`px-2.5 py-1 text-xs rounded-md transition-colors ${effectiveChart === ct ? 'bg-accent-muted text-accent-text' : 'text-text-muted hover:bg-page'}`}
            >
              {ct}
            </button>
          ))}
        </div>

        {effectiveChart !== 'table' && yKey && (
          <div className="bg-surface border border-border rounded-lg p-4">
            {effectiveChart === 'bar' && <BarChart data={result.rows} xKey={xKey} yKey={yKey} />}
            {effectiveChart === 'line' && <LineChart data={result.rows} xKey={xKey} yKey={yKey} />}
            {effectiveChart === 'stacked' && <StackedBarChart data={result.rows} xKey={xKey} stackKeys={numericCols} />}
          </div>
        )}

        <DataTable columns={result.columns} rows={result.rows} />

        <details className="text-xs text-text-muted">
          <summary className="cursor-pointer">Show generated SQL</summary>
          <pre className="mt-2 bg-page border border-border rounded-md p-3 font-mono text-[11px] overflow-x-auto whitespace-pre-wrap">{result.sql}</pre>
        </details>
      </div>

      <Modal
        open={saveOpen}
        onClose={() => setSaveOpen(false)}
        title="Save custom report"
        footer={<>
          <Button onClick={() => setSaveOpen(false)}>Cancel</Button>
          <Button variant="primary" onClick={async () => {
            try {
              await api.saveReport({
                name: displayName,
                reportType: 'custom',
                sqlQuery: result.sql,
                chartType: effectiveChart,
              });
              showToast('success', `Saved "${displayName}"`);
              setSaveOpen(false);
            } catch (err: any) {
              showToast('error', err.message ?? 'Save failed');
            }
          }}>Save</Button>
        </>}
      >
        <div className="text-sm text-text-secondary">Save this report so you can re-run it or schedule it later.</div>
      </Modal>
    </div>
  );
}
